import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { icons } from 'seal-ui-components';
import { withSize } from 'react-sizeme';
import Permission from '@Constants/permissions';
import getClauseLibrary from '@Actions/clauseActions';
import { AuthorizePage } from '@Components/Authorize';
import { isAuthorized } from '@Service/authentication';
import style, { AllGroupsTexts, TitleTreeWrapper } from './ClauseTree.style';
import ClauseTreeSearch from './ClauseTreeSearch';
import { ClauseTree as ClauseTreeList } from './components/Tree';
import { ClauseList } from './components/List';
import mixpanelEvents from '@Mixpanel';
import { MIX_PANEL } from '@Constants/types';
import { usePermissions } from '@Context';

const ClauseTree = ({ className }) => {
    const dispatch = useDispatch();
    const [headerHeight, setHeaderHeight] = useState(0);
    const [titleHeight, setTitleHeight] = useState(0);

    const permissions = [Permission.CLAUSE_READ];

    const { haveAccessToClausesPage } = usePermissions();

    const [searchResults, setSearchResults] = useState([]);

    const { defaultClauseGroups, flattenClauseGroups, clauseLibraryLoadedState } = useSelector(
        state => state.clause
    );

    const { user } = useSelector(state => state.auth);

    useEffect(() => {
        if (isAuthorized(user, permissions)) {
            if (clauseLibraryLoadedState !== 'Loaded') dispatch(getClauseLibrary());
        }
    }, []);

    useEffect(
        () => {
            if (clauseLibraryLoadedState === 'Loaded') {
                const mixpanel = { totalNumberParentClauseGroups: defaultClauseGroups.length };
                dispatch({ type: MIX_PANEL.MIXPANEL_DATA_CLAUSE_LIBRARY, payload: mixpanel });
                mixpanelEvents.clauselibraryDisplayed();
            }
        },
        [flattenClauseGroups]
    );

    return (
        <div className={className}>
            <AuthorizePage havePermission={haveAccessToClausesPage}>
                <div className="clause-tree">
                    <ClauseTreeSearch
                        setSearchResults={setSearchResults}
                        onSize={size => setHeaderHeight(size.height)}
                    />
                    <ClauseTreeHeader
                        searchResults={searchResults}
                        defaultClauseGroups={defaultClauseGroups}
                        onSize={size => setTitleHeight(size.height)}
                    />
                    {searchResults.length ? (
                        <ClauseList
                            flattenClauseGroups={flattenClauseGroups}
                            searchResults={searchResults}
                            headerHeight={headerHeight + titleHeight}
                        />
                    ) : (
                        <TitleTreeWrapper>
                            <ClauseTreeList
                                data={defaultClauseGroups}
                                flattenClauseGroups={flattenClauseGroups}
                            />
                        </TitleTreeWrapper>
                    )}
                </div>
                {clauseLibraryLoadedState === 'Loading' && (
                    <div className="spinner-wrapper">
                        <icons.LoadingLargeIcon />
                    </div>
                )}
            </AuthorizePage>
        </div>
    );
};

const ClauseTreeHeader = withSize({ monitorHeight: true })(
    ({ searchResults, defaultClauseGroups }) => (
        <AllGroupsTexts>
            {searchResults.length > 0
                ? `${searchResults.length} match(es)`
                : `All clause groups (${Object.keys(defaultClauseGroups).length})`}
        </AllGroupsTexts>
    )
);

export default styled(ClauseTree)`
    ${style};
`;
